import React, { useState, useCallback, useEffect } from 'react';
import dayjs from 'dayjs';
import { 
  Button, 
  Input, 
  Space, 
  Row, 
  Col, 
  Upload, 
  message,
  Typography 
} from 'antd';
import { 
  DownloadOutlined, 
  UploadOutlined, 
  SearchOutlined 
} from '@ant-design/icons';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import AdminLayout from '@/components/Layouts/AdminLayout/AdminLayout';
import StudentTable from '@/pages/admin/components/table/StudentTable';
import { academyStudentApi, adminStudentApi } from '@/services/api';
import { Modal } from '@/components/organisms/Modal/Modal';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

interface StudentData {
  key: string;
  no: number;
  academyCode: string;
  studentName: string;
  studentId: string; // 실제 로그인 아이디 표시
  academyStudentId: string; // 내부 식별용
  birthDate: string;
  status: 'active' | 'inactive' | 'withdrawn';
  startDate: string;
}

const formatDate = (value?: string | number | Date | null) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '-';
};

const mapStatus = (value?: string): 'active' | 'inactive' | 'withdrawn' => {
  if (!value) return 'active';
  const lower = value.toLowerCase();
  if (lower.includes('퇴원') || lower.includes('withdraw')) return 'withdrawn';
  if (lower.includes('중지') || lower.includes('suspend')) return 'inactive';
  return 'active';
};

const StudentManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialKeyword = searchParams.get('q') || '';
  const initialPage = Number(searchParams.get('page')) || 1;
  const initialPageSize = Number(searchParams.get('size')) || DEFAULT_PAGE_SIZE;
  const [searchText, setSearchText] = useState(initialKeyword);
  const [rows, setRows] = useState<StudentData[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [appliedKeyword, setAppliedKeyword] = useState(initialKeyword);
  const academyCode = searchParams.get('academyCode') || '';

  const syncSearchParams = useCallback((nextPage: number, nextSize: number, keyword: string) => {
    const params = new URLSearchParams();
    if (keyword) params.set('q', keyword);
    if (academyCode) params.set('academyCode', academyCode);
    if (nextPage > 1) params.set('page', String(nextPage));
    if (nextSize !== DEFAULT_PAGE_SIZE) params.set('size', String(nextSize));
    setSearchParams(params);
  }, [academyCode, setSearchParams]);

  const fetchList = useCallback(async (p: number, s: number, keyword: string) => {
    try {
      const res: any = await adminStudentApi.listAll({
        page: p - 1,
        size: s,
        q: keyword || undefined,
        academyCode: academyCode ? academyCode : undefined,
      });
      const pageData = res?.data || res;
      const content = pageData?.content || [];
      const mapped: StudentData[] = content.map((it: any, idx: number) => ({
        key: String((pageData?.totalElements ?? content.length) - ((p - 1) * s + idx)),
        no: (pageData?.totalElements ?? content.length) - ((p - 1) * s + idx),
        academyCode: it.academyCode || '-',
        studentName: it.name || it.studentName || '-',
        studentId: it.studentLoginId || it.studentId || it.loginId || '-',
        academyStudentId: String(it.academyStudentId || it.id || ''),
        birthDate: formatDate(it.birthDate),
        status: mapStatus(it.status || it.enrollmentStatus),
        startDate: formatDate(it.registrationDate ?? it.enrolledAt),
      }));
      setRows(mapped);
      setTotal(pageData?.totalElements ?? mapped.length);
      setPage(pageData?.number != null ? (pageData.number + 1) : p);
      setPageSize(pageData?.size ?? s);
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotal(0);
    }
  }, [academyCode]);

  useEffect(() => {
    syncSearchParams(page, pageSize, appliedKeyword);
    fetchList(page, pageSize, appliedKeyword);
  }, [academyCode, page, pageSize, appliedKeyword, fetchList, syncSearchParams]);

  const handleSearch = () => {
    const keyword = searchText.trim();
    setPage(1);
    setAppliedKeyword(keyword);
  };

  const handleReset = () => {
    setSearchText('');
    setAppliedKeyword('');
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await academyStudentApi.downloadTemplate({ includeAcademyCode: true });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement('a');
      a.href = url; a.download = 'student_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { message.error('템플릿 다운로드 실패'); }
  };

  const confirmGuardianConsent = async () => {
    return Modal.confirm({
      message: '본인은 만 14세 미만의 학생에 대한 학부모(법정대리인) 동의를 받고\n계정 정보를 등록함을 확인합니다.',
      okText: '동의',
      cancelText: '취소',
    });
  };

  const handleUploadExcel = async (file: File) => {
    const consent = await confirmGuardianConsent();
    if (!consent) {
      return Upload.LIST_IGNORE;
    }
    try {
      const effectiveAcademyCode = academyCode || undefined;
      await academyStudentApi.uploadExcel({ academyCode: effectiveAcademyCode, file });
      await Modal.alert({
        message: '업로드 및 등록이 완료되었습니다.(중복아이디는 등록불가)',
        okText: '확인',
      });
      fetchList(1, pageSize, appliedKeyword);
    } catch (e: any) {
      const serverMessage = e?.response?.data?.message;
      const alertMessage = serverMessage || '형식이 올바르지 않습니다.';
      await Modal.alert({
        title: '업로드 실패',
        message: alertMessage,
        okText: '확인',
      });
    }
    return Upload.LIST_IGNORE;
  };



  return (
    <AdminLayout>
      <div style={{ padding: '24px' }}>
      <Row gutter={16} justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Title level={2}>
          학생관리
        </Title>

        {/* 버튼 영역 */}
        <Row gutter={16}>
          <Col>
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={handleDownloadTemplate}
              size="large"
            >
              학생계정생성 양식 다운로드
            </Button>
          </Col>
          <Col>
            <Upload
              beforeUpload={handleUploadExcel}
              accept=".xlsx,.xls"
              showUploadList={false}
            >
              <Button 
                icon={<UploadOutlined />}
                size="large"
              >
                학생 계정 생성 엑셀 업로드
              </Button>
            </Upload>
          </Col>
        </Row>
      </Row>


        {/* 검색 영역 */}
        <Row gutter={16} align="middle" style={{ marginBottom: '16px' }}>
          <Col span={6}>
            <Input
              placeholder="학생명, 학생ID, 학원코드로 검색"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onPressEnter={handleSearch}
              prefix={<SearchOutlined />}
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch} icon={<SearchOutlined />}>
                검색
              </Button>
              <Button onClick={handleReset}>
                초기화
              </Button>
            </Space>
          </Col>
        </Row>

        {/* 테이블 */}
        <StudentTable 
          students={rows}
          loading={false}
          type="management"
          pagination={{ 
            total, 
            current: page, 
            pageSize,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: false
          }}
          onRow={(record) => ({
            onClick: () => navigate(
              `/admin/student/detail/${record.academyStudentId}`,
              { state: { from: `${location.pathname}${location.search}` } },
            ),
          })}
        />
      </div>
    </AdminLayout>
  );
};

export default StudentManagement; 